/* eslint-disable */
import { action, computed, IReactionDisposer, observable } from 'mobx';
import { GridData, GridFilters } from './DataState';
import { ColumnResizing, IdType, SortingRule, TableState } from 'react-table';
import { DataQuery, DataQueryResult } from './DataQuery';
import { Identifiable } from '@custis/common';
import {
  ExpandMode,
  FilterMapping,
  IHiddenColumn,
  SELECTION_COLUMN_ID,
  SortingMapping,
  TFilterWithId,
  TTableColumn,
  TTableRowMode,
} from './models';
import { Key, RefObject } from 'react';
import { FixedSizeList } from 'react-window';
import { isNotEmpty } from './utils';
import { GridDataStore, GridState, GridStateStore } from './stores';
import { difference, isEqual, isNil } from 'lodash';
import { FilterMapper, SortingMapper } from './mappers';
import { FilterService, PaginationService } from './services';

type StateColumnMinimalType = { id?: string | undefined };

export interface StateStorage<T> {
  value?: T;
  set(
    state: T,
    options?: {
      replaceHistory: boolean;
    },
  ): void;
}

export abstract class GridStore<
  TItem extends Identifiable & { children?: TItem[] },
  TFilter,
  TSorting,
  TResultAdditionalInfo = undefined,
> {
  abstract lsStatekey: string;
  private filterMapper = new FilterMapper<TItem, TFilter>();
  private sortingMapper = new SortingMapper<TItem, TSorting>();
  pagingService = new PaginationService<TItem>();
  private filterService = new FilterService();

  public subRowIdSeparator: string = '-';

  @observable isLoading = false;
  @observable scrollLeft = 0;
  // Не удаляйте = undefined: без этого mobx перестает отслеживать изменения
  @observable newRowData?: Partial<TItem> = undefined;
  // Не удаляйте = undefined: без этого mobx перестает отслеживать изменения
  @observable editRowData?: Partial<TItem> = undefined;

  protected abortController?: AbortController;

  protected gridStateStore = new GridStateStore<TItem, TFilter, TSorting>();

  protected gridData: GridData<TItem, TResultAdditionalInfo> = new GridDataStore<TItem, TResultAdditionalInfo>();

  protected disposers: IReactionDisposer[] = [];

  @observable.ref selectedItemsKeys: Record<IdType<TItem>, boolean> = {} as Record<IdType<TItem>, boolean>;

  // Не удаляйте = undefined: без этого mobx перестает отслеживать изменения
  @observable urlState?: StateStorage<DataQuery<TFilter, TSorting>> = undefined;

  // v Можно вынести в отдельный класс
  toggleRowSelected?: (rowId: IdType<TItem>, set?: boolean) => void;
  rowCanBeSelected: (row: TItem) => boolean = () => true;
  getSubRow: ((originalRow: TItem, relativeIndex?: number) => TItem[]) | undefined;
  fixedSizeList?: RefObject<FixedSizeList | null>;
  nonVirtualList?: RefObject<HTMLDivElement | null>;

  @action
  setSelection(id: string, value: boolean) {
    if (this.toggleRowSelected != null) {
      this.toggleRowSelected(id, value);
      this.setChecked(id, value);
    }
  }

  @action
  toggleSelectAll(value: boolean) {
    this.result.items.forEach((e) => {
      if (this.rowCanBeSelected(e)) {
        this.setSelection(e.id.toString(), value);
      }
      if (this.getSubRow) {
        const subrows = this.getSubRow(e);
        if (subrows) {
          subrows.forEach((sub) => {
            if (this.rowCanBeSelected(sub)) {
              this.setSelection(e.id.toString() + this.subRowIdSeparator + sub.id.toString(), value);
            }
          });
        }
      }
    });
  }

  @computed({ keepAlive: true })
  get isAllSelected(): boolean {
    const possibleSelected = this.result.items.filter(this.rowCanBeSelected);
    const isAllParentRowsSelected = possibleSelected.every((item) => this.selectedItemsKeys[item.id]);
    const isAllChildRowsSelected = this.result.items
      .reduce<boolean[]>((acc, parent) => {
        if (parent.children) {
          const children = parent.children
            .filter(this.rowCanBeSelected)
            .every(
              (child) => this.selectedItemsKeys[parent.id.toString() + this.subRowIdSeparator + child.id.toString()],
            );
          acc.push(children);
        }
        return acc;
      }, [])
      .every((item) => item);
    return this.result.items.length > 0 && isAllChildRowsSelected && isAllParentRowsSelected;
  }

  @observable
  stateRestored = false;

  // move to another service ?
  @action
  loadResizing(): void {
    const resizingRaw = localStorage.getItem(this.lsStatekey);
    const resizing = this.getSafeFromJSON<ColumnResizing>(resizingRaw);
    if (resizing) {
      this.gridState.outerColumnResizing = resizing;
    }
  }

  // move to another service ?
  @action
  setColumnsResizing(resizing: ColumnResizing | undefined) {
    if (resizing && resizing.columnWidths && Object.keys(resizing.columnWidths).length > 0) {
      localStorage.setItem(this.lsStatekey, JSON.stringify(resizing));
    }
  }

  @action
  setTableState(tableState: TableState<TItem>) {
    this.gridState.tableState = tableState;
  }

  @action
  setColumns(columns: TTableColumn<TItem, TFilter, TSorting>[]) {
    this.gridState.columns = columns;
  }

  @action
  setColumnOrder(columnOrder: IdType<TItem>[]) {
    localStorage.setItem(this.orderKey, JSON.stringify(columnOrder));
    this.gridState.columnOrder = columnOrder;
  }

  @computed
  get gridState(): GridState<TItem, TFilter, TSorting> {
    return this.gridStateStore;
  }

  @computed
  get result(): DataQueryResult<TItem, TResultAdditionalInfo> {
    return this.gridData.data;
  }

  @action
  setChecked(id: string, value: boolean): void {
    this.selectedItemsKeys = {
      ...this.selectedItemsKeys,
      [id]: value,
    };
    localStorage.setItem(this.checkedKey, JSON.stringify(this.selectedItemsKeys));
  }

  @action
  setExpanded(id: number, value: boolean) {
    this.gridState.expanded = {
      ...this.gridState.expanded,
      [id]: value,
    };
    this.saveExpanded(this.gridState.expanded);
  }

  @action
  toggleAllExpand(mode: ExpandMode): void {
    let expanded = false;
    if ([ExpandMode.MIXED, ExpandMode.CLOSED].includes(mode)) {
      expanded = true;
      this.gridState.expandedAllMode = ExpandMode.OPENED;
    }
    if (mode === ExpandMode.OPENED) {
      expanded = false;
      this.gridState.expandedAllMode = ExpandMode.CLOSED;
    }
    this.result.items.forEach((item) => {
      this.setExpanded(item.id, expanded);
    });
    this.gridState.expanded = Object.keys(this.gridState.expanded).reduce<Record<string, boolean>>((acc, curr) => {
      acc[curr] = expanded;

      return acc;
    }, {});
    localStorage.setItem(this.expandAllKey, this.gridState.expandedAllMode);
  }

  saveHiddenColumnsToLocalStorage(columns: IHiddenColumn<TItem>[]): void {
    localStorage.setItem(this.hiddenKey, JSON.stringify(columns));
  }

  saveExpanded(values: Record<string, boolean>): void {
    localStorage.setItem(this.expandedKey, JSON.stringify(values));
  }

  @action
  toggleHiddenColumn(columnId?: IdType<TItem>, visible?: boolean, touched?: boolean): void {
    if (columnId) {
      this.gridState.hiddenColumns = this.gridState.hiddenColumns.map((item) => ({
        title: item.title,
        visible: item.title === columnId ? (typeof visible !== 'undefined' ? visible : !item.visible) : item.visible,
        touched: item.title === columnId ? (typeof touched !== 'undefined' ? touched : !item.visible) : item.touched,
      }));
      this.saveHiddenColumnsToLocalStorage(this.gridState.hiddenColumns);
    }
  }

  @action
  toggleAllHiddenColumns(value: boolean): void {
    this.gridState.hiddenColumns = this.gridState.hiddenColumns.map((item) => ({
      ...item,
      visible: value,
      touched: true,
    }));
    this.saveHiddenColumnsToLocalStorage(this.gridState.hiddenColumns);
  }

  getColumnVisibility(columnId?: IdType<TItem>): IHiddenColumn<TItem> | undefined {
    return this.gridState.hiddenColumns.find((item) => item.title === columnId);
  }

  get expandedKey(): string {
    return `${this.lsStatekey}-expanded`;
  }

  get expandAllKey(): string {
    return `${this.lsStatekey}-expandedAll`;
  }

  get checkedKey(): string {
    return `${this.lsStatekey}-checked`;
  }

  get hiddenKey(): string {
    return `${this.lsStatekey}-hidden`;
  }

  get orderKey(): string {
    return `${this.lsStatekey}-order`;
  }

  setPaging(pageSize: number, pageIndex: number): void {
    this.urlState?.set({
      filter: this.urlState.value?.filter,
      paging: {
        count: true,
        skip: pageIndex * pageSize,
        top: pageSize,
        pageIndex: pageIndex,
      },
      sorting: this.urlState.value?.sorting,
      selection: {
        highlightedRowId: this.urlState?.value?.selection?.highlightedRowId,
      },
    });
    void this.loadData();
  }

  @action
  setFilters(filters: GridFilters<TItem>): void {
    const nonGridFilters = this.filterService.getNonGridFilters(
      this.urlState?.value?.filter,
      Object.values(this.filterMapping).map((item) => String(item)),
    );

    if (
      !this.filterService.isEqual(this.urlState?.value?.filter, {
        ...this.filterMapper.map(filters, this.filterMapping),
        ...nonGridFilters,
      })
    ) {
      this.urlState?.set({
        filter: {
          ...this.filterMapper.map(filters, this.filterMapping),
          ...nonGridFilters,
        },
        paging: this.urlState.value?.paging,
        sorting: this.urlState.value?.sorting,
        selection: {
          highlightedRowId: this.urlState?.value?.selection?.highlightedRowId,
        },
      });
      void this.loadData();
    }
  }

  @action
  updateFilters(filter: Record<string, unknown>): void {
    const urlStateFilter = this.urlState?.value?.filter ?? {};
    const excludedFilterKeys = Object.entries(filter)
      .filter(([, value]) => isNil(value))
      .map(([key]) => key);

    const stateFilterWithoutExcluded = Object.fromEntries(
      Object.entries(urlStateFilter).filter(([key]) => !excludedFilterKeys.includes(key)),
    ) as Partial<TFilter>;
    const nonNilUpdatedFilter = Object.fromEntries(
      Object.entries(filter).filter(([, value]) => !isNil(value)),
    ) as Partial<TFilter>;

    const updatedUrlStateFilter = { ...stateFilterWithoutExcluded, ...nonNilUpdatedFilter };

    this.urlState?.set({
      filter: updatedUrlStateFilter as TFilter,
      paging: this.urlState.value?.paging,
      sorting: this.urlState.value?.sorting,
      selection: {
        highlightedRowId: this.urlState?.value?.selection?.highlightedRowId,
      },
    });
    void this.loadData();
  }

  @action
  setSorting(sortBy: SortingRule<TSorting>[]): void {
    this.urlState?.set({
      filter: this.urlState?.value?.filter,
      paging: this.urlState?.value?.paging,
      sorting: this.sortingMapper.map(sortBy, this.sortingMapping),
      selection: {
        highlightedRowId: this.urlState?.value?.selection?.highlightedRowId,
      },
    });
    void this.loadData();
  }

  @action
  setHighlightedRowId(id: string | undefined): void {
    this.gridState.highlightedId = id;
    this.urlState?.set(
      {
        filter: this.urlState?.value?.filter,
        paging: this.urlState?.value?.paging,
        sorting: this.urlState?.value?.sorting,
        selection: {
          highlightedRowId: id,
        },
      },
      { replaceHistory: true },
    );
  }

  onDestroy() {
    this.disposers.forEach((disposer) => disposer());
  }

  onDragEnd(from: number, to: number) {
    const colOrder = [...this.gridState.columnOrder];
    const visibleColumnsWithOrder = colOrder.reduce<IdType<TItem>[]>((acc, curr) => {
      if (!this.hiddenColumns.includes(curr)) {
        acc.push(curr);
      }
      return acc;
    }, []);
    const fromColumnName = visibleColumnsWithOrder[from];
    const toColumnName = visibleColumnsWithOrder[to];
    const fromColumnIndex = colOrder.findIndex((item) => item === fromColumnName);
    const toColumnIndex = colOrder.findIndex((item) => item === toColumnName);
    const item = colOrder.splice(fromColumnIndex, 1)[0];
    colOrder.splice(toColumnIndex, 0, item);
    this.setColumnOrder(colOrder);
  }

  abstract executeQuery(
    query: DataQuery<TFilterWithId<TFilter>, TSorting>,
    signal?: AbortSignal,
  ): Promise<DataQueryResult<TItem, TResultAdditionalInfo>>;

  @computed
  get filterMapping(): FilterMapping<TItem, TFilter> {
    return this.gridState.columns.reduce<FilterMapping<TItem, TFilter>>(
      (acc, curr) => {
        if (curr.id) {
          if (curr.filterName) {
            acc[curr.id as keyof TItem] = curr.filterName;
          }
          // для случаев, когда есть колонки приходят с бэка
          if (curr.extraColumn) {
            // @ts-ignore
            acc[curr.id] = curr.id;
          }
        }
        return acc;
      },
      {} as FilterMapping<TItem, TFilter>,
    );
  }

  @computed
  get sortingMapping(): SortingMapping<TItem, TSorting> {
    return this.gridState.columns.reduce<SortingMapping<TItem, TSorting>>(
      (acc, curr) => {
        if (curr.sortingName && curr.id) {
          acc[curr.id as keyof TItem] = curr.sortingName;
        }
        return acc;
      },
      {} as SortingMapping<TItem, TSorting>,
    );
  }

  @action
  async loadData(): Promise<void> {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    const signal = this.abortController!.signal;
    this.isLoading = true;
    const state = this.urlState?.value;
    try {
      const result = await this.executeQuery(
        {
          paging: state?.paging,
          filter: state?.filter as any,
          sorting: state?.sorting,
          selection: state?.selection,
        },
        signal,
      );
      if (state?.paging?.top) {
        this.gridState.pageCount = this.pagingService.getPagingCount(state?.paging?.top, result.totalCount);
      }
      this.gridData.data = result;
    } catch (e) {
      console.error(e);
      this.gridData.data = { items: [] };
    } finally {
      this.isLoading = signal.aborted;
    }
  }

  @action
  loadCheckedRows(): void {
    try {
      const checkedRowsString = localStorage.getItem(this.checkedKey);
      if (checkedRowsString) {
        this.selectedItemsKeys = JSON.parse(checkedRowsString);
      }
    } catch (e) {
      console.error(e);
    }
  }

  @action
  loadExpandedRows(): void {
    try {
      const expandedRowsString = localStorage.getItem(this.expandedKey);
      const expandAllKey = localStorage.getItem(this.expandAllKey);
      this.gridState.expandedAllMode = (expandAllKey as ExpandMode) ?? ExpandMode.CLOSED;
      if (expandedRowsString) {
        this.gridState.expanded = JSON.parse(expandedRowsString);
        if ([ExpandMode.OPENED, ExpandMode.CLOSED].includes(this.gridState.expandedAllMode)) {
          this.gridState.expanded = Object.keys(this.gridState.expanded).reduce<Record<string, boolean>>(
            (acc, curr) => {
              acc[curr] = this.gridState.expandedAllMode === ExpandMode.OPENED;
              return acc;
            },
            {},
          );
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  @action
  loadHiddenColumns(): void {
    try {
      const hiddenColumnsString = localStorage.getItem(this.hiddenKey);
      if (hiddenColumnsString) {
        const columnsFromLocalStorage = JSON.parse(hiddenColumnsString);
        const mapOldStructure = (item: StateColumnMinimalType) => ({
          title: item.id ?? '',
          visible: !columnsFromLocalStorage.includes(item.id),
          touched: columnsFromLocalStorage.includes(item.id),
        });

        // раньше тип скрытых колонк был IdType<TItem>[]
        // здесь мы проверяем
        const isOldStructure =
          Array.isArray(columnsFromLocalStorage) && columnsFromLocalStorage.every((item) => typeof item === 'string');
        if (isOldStructure) {
          // и переводим в новый тип, если надо
          this.gridState.hiddenColumns = this.gridState.columns.map(mapOldStructure);
        } else {
          const newColumns = findNewColumns(this.gridState.columns, columnsFromLocalStorage);
          if (newColumns.length > 0) {
            this.gridState.hiddenColumns = [...columnsFromLocalStorage, ...newColumns.map(mapColumns)];
          } else {
            this.gridState.hiddenColumns = columnsFromLocalStorage;
          }
        }
      } else {
        this.gridState.hiddenColumns = this.gridState.columns.map(mapColumns);
      }
    } catch {
      this.gridState.hiddenColumns = this.gridState.columns.map(mapColumns);
    }
    this.saveHiddenColumnsToLocalStorage(this.gridState.hiddenColumns);
  }

  //todo: вынести в отдельный сервис, например ColumnOrderService
  @action
  loadOrderColumns(columns: TTableColumn<TItem, TFilter, TSorting>[], hasGridSelectionColumn: boolean): void {
    try {
      const orderColumnsString = localStorage.getItem(this.orderKey);
      const allColumnIds = columns.map((item) => item.id).filter(isNotEmpty);
      //todo: добавить функционал для, который будет учитывать появление/удаление новых колонок
      // чтобы в localStorage'e хранились только существующие колонки
      if (orderColumnsString) {
        try {
          this.gridState.columnOrder = JSON.parse(orderColumnsString);
        } catch (e) {
          this.gridState.columnOrder = allColumnIds;
        }
        const diff = difference<string>(this.gridState.columnOrder, allColumnIds);
        if (diff.length > 0) {
          diff.forEach((item) => {
            this.gridState.columnOrder = this.gridState.columnOrder.filter((col) => col !== item);
          });
        }
        allColumnIds.forEach((column) => {
          if (!this.gridState.columnOrder.includes(column)) {
            this.gridState.columnOrder = [...this.gridState.columnOrder, column];
          }
        });
        const hasSelectionInSavedColumns = this.gridState.columnOrder.includes(SELECTION_COLUMN_ID);
        if (hasGridSelectionColumn && !hasSelectionInSavedColumns) {
          this.gridState.columnOrder = [SELECTION_COLUMN_ID, ...this.gridState.columnOrder];
        }
        // this.setColumnOrder(this.gridState.columnOrder);
      } else {
        this.gridState.columnOrder = allColumnIds;
        if (hasGridSelectionColumn) {
          this.gridState.columnOrder = [SELECTION_COLUMN_ID, ...this.gridState.columnOrder];
        }
      }
    } catch {
      this.gridState.columnOrder = [];
    }
    this.setColumnOrder(this.gridState.columnOrder);
  }

  @action
  changeTableMode(value: TTableRowMode): void {
    this.gridState.tableRowMode = value;
  }

  @action
  onHideEditRow(): void {
    this.changeTableMode('view');
    this.newRowData = undefined;
    this.editRowData = undefined;
  }

  @computed
  get areAllColumnsHidden(): boolean {
    const hiddenColumnsSorted = this.gridState.hiddenColumns.sort();
    const columnsIdsSorted = this.gridState.columns
      .filter((item) => item.id !== SELECTION_COLUMN_ID)
      .map((item) => item.id)
      .sort();
    return isEqual(hiddenColumnsSorted, columnsIdsSorted);
  }

  @action
  onNewRowChange(row: Partial<TItem>): void {
    const rowWithValue = Object.entries(row).reduce<{ [key: string]: Key | boolean }>((acc, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {}) as unknown as Partial<TItem>;
    this.newRowData = {
      ...this.newRowData,
      ...rowWithValue,
    };
  }

  @action
  onEditRowChange(row: Partial<TItem>): void {
    this.editRowData = {
      ...this.editRowData,
      ...row,
    };
  }

  @action
  setUrlState(urlState: StateStorage<DataQuery<TFilter, TSorting>>) {
    this.urlState = urlState;
  }

  @action
  createRow(): void {
    this.changeTableMode('create');
    this.setHighlightedRowId(undefined);
    this.gridState.columns.forEach((item) => {
      if (item.id && this.hiddenColumns.includes(item.id) && item.required) {
        this.toggleHiddenColumn(item.id, true, true);
      }
    });
  }

  @action
  createSubRow(newRowId: number): void {
    this.gridState.subRowId = newRowId;
    this.changeTableMode('create-sub-row');
  }

  @computed
  get hiddenColumns(): IdType<TItem>[] {
    return Array.from(this.gridState.hiddenColumns)
      .filter((item) => !item.visible)
      .map((item) => item.title);
  }

  @action
  async highlightModificatedRow(id?: number): Promise<void> {
    if (id) {
      const createdNodeResponse = await this.executeQuery({
        filter: {
          id: [id],
        } as TFilterWithId<TFilter>,
        sorting: undefined,
        paging: undefined,
        selection: undefined,
      });
      const createdNode = createdNodeResponse.items[0];
      if (createdNode) {
        const createdNodeIndex = this.result.items.findIndex((item) => item.id === createdNode.id);
        if (createdNodeIndex >= 0) {
          this.fixedSizeList?.current?.scrollTo(100);
          this.nonVirtualList?.current?.scrollTo({ top: 100 });
        } else {
          this.gridData.data = {
            items: [createdNode, ...this.result.items],
            totalCount: (this.result?.totalCount ?? 0) + 1,
            additionalInfo: this.result.additionalInfo,
          };
        }
        this.urlState?.set({
          filter: this.urlState?.value?.filter,
          paging: this.urlState?.value?.paging,
          sorting: this?.urlState?.value?.sorting,
          selection: {
            highlightedRowId: id.toString(),
          },
        });
      }
    }
  }

  @action
  resetGrid(): void {
    this.gridState.tableRowMode = 'view';
    this.gridState.highlightedId = undefined;
  }

  @computed get highlightedItem(): TItem | undefined {
    const highlightedId = this.gridState.highlightedId;
    if (highlightedId) {
      if (highlightedId.includes(this.subRowIdSeparator)) {
        const [parentId, childId] = highlightedId.split(this.subRowIdSeparator);
        const parent = this.result.items.find((item) => item.id === parseInt(parentId));
        return parent?.children?.find((item) => item.id === parseInt(childId));
      } else {
        return this.result.items.find((item) => item.id === parseInt(highlightedId));
      }
    } else {
      return undefined;
    }
  }

  private getSafeFromJSON<T>(value: string | undefined | null): T | undefined {
    if (value == null) {
      return undefined;
    }
    try {
      return JSON.parse(value) as T;
    } catch (e) {
      return undefined;
    }
  }
}

function mapColumns(item: { id?: string | undefined }) {
  return {
    title: item.id ?? '',
    visible: true,
    touched: false,
  };
}

function findNewColumns(state: StateColumnMinimalType[], storage: IHiddenColumn<string>[]) {
  const keysInStorage = storage.map((i) => i.title);
  return state.filter((i) => !keysInStorage.includes(i.id ?? ''));
}
